// Formatos en espanol de Colombia: moneda, duraciones, fechas relativas, iniciales.
// Fechas y horas se muestran en la zona horaria elegida en Mi perfil (por defecto, la del navegador).
import { claveDia, partesEn, sumarDias, zonaActual } from './zonaHoraria';

const copFormat = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

export const cop = (valor: number) => copFormat.format(Math.round(valor || 0));

export function money(valor: number, moneda = 'COP') {
  try {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: moneda || 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: moneda === 'COP' || Number.isInteger(valor) ? 0 : 2,
    }).format(valor || 0);
  } catch {
    return cop(valor);
  }
}

export const fromMicros = (micros: number | null | undefined) => (micros ?? 0) / 1_000_000;
export const toMicros = (valor: number) => Math.round(valor * 1_000_000);

export function copShort(v: number) {
  if (!v) return '0';
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;
  if (Math.abs(v) >= 1e3) return `${Math.round(v / 1000)} mil`;
  return String(Math.round(v));
}

const pad = (n: number) => String(n).padStart(2, '0');

// 75 -> "1:15", 3725 -> "1:02:05"
export function duration(segundos: number | null | undefined) {
  const s = Math.max(0, Math.round(segundos ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h ? `${h}:${pad(m)}` : m}:${pad(s % 60)}`;
}

export const time = (fecha: string | number | Date) =>
  new Date(fecha).toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit', timeZone: zonaActual() });

export const shortDate = (fecha: string | number | Date) =>
  new Date(fecha).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', timeZone: zonaActual() });

export function isToday(fecha: string | number | Date | null | undefined, ahora = new Date()) {
  if (!fecha) return false;
  return claveDia(new Date(fecha)) === claveDia(ahora);
}

export function isYesterday(fecha: string | number | Date | null | undefined, ahora = new Date()) {
  if (!fecha) return false;
  return claveDia(new Date(fecha)) === sumarDias(claveDia(ahora), -1);
}

export function ago(fecha: string | number | Date | null | undefined, ahora = Date.now()) {
  if (!fecha) return '';
  const min = Math.floor((ahora - new Date(fecha).getTime()) / 60000);
  if (min < 0) {
    const futuro = -min;
    if (futuro < 60) return `en ${futuro} min`;
    if (futuro < 1440) return `en ${Math.round(futuro / 60)} h`;
    return `en ${Math.round(futuro / 1440)} días`;
  }
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  if (min < 1440) return `hace ${Math.round(min / 60)} h`;
  if (min < 2880) return 'ayer';
  return `hace ${Math.round(min / 1440)} días`;
}

// Fecha de vencimiento de una tarea en lenguaje natural.
export function dueLabel(fecha: string | null | undefined, ahora = new Date()) {
  if (!fecha) return 'Sin fecha';
  const d = new Date(fecha);
  if (isToday(d, ahora)) return `Hoy ${time(d)}`;
  if (claveDia(d) === sumarDias(claveDia(ahora), 1)) return `Mañana ${time(d)}`;
  if (d < ahora) return `Vencida · ${shortDate(d)}`;
  return shortDate(d);
}

export const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function initials(nombre: string) {
  const palabras = String(nombre || '')
    .trim()
    .split(/\s+/)
    .filter((w) => /^[\p{L}]/u.test(w));
  const letras = palabras.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
  if (letras) return letras;
  const digitos = String(nombre || '').replace(/\D/g, '');
  return digitos ? digitos.slice(-2) : '#';
}

// Tono estable por nombre, para colorear avatares sin guardar nada.
export function hue(texto: string) {
  let h = 0;
  for (const c of String(texto)) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}

export const normalize = (s: string) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();

export function greeting(ahora = new Date()) {
  const h = partesEn(ahora).hora;
  if (h < 12) return 'Buenos días';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

export function plural(n: number, uno: string, varios: string) {
  return `${n.toLocaleString('es-CO')} ${n === 1 ? uno : varios}`;
}
